/**
 * Vendor portal: one quote upload per structure in a multi-structure package.
 *
 * A Files → RFQs round publishes ONE package per supplier covering every structure in
 * the request (the same is true of a CNC package scoped to several structures). The
 * supplier still prices each structure on its own, so the portal offers an upload slot
 * per structure — timber and CNC alike — and reports which structures are quoted.
 */
import type { RfqPackage, RfqProject, RfqSubmission, TimberSupplierStructureRef } from '../types';
import { submissionQuoteTotal } from './rfqQuoteTotal';
import { normalizeQuotedStructures, timberQuoteAssignableStructures } from './timberQuoteStreamKey';

export interface VendorStructureQuoteSlot {
  structure: TimberSupplierStructureRef;
  /** Newest live quote tagged to exactly this structure (not a whole-request upload). */
  latest: RfqSubmission | null;
  /** Every live quote tagged to exactly this structure, newest first. */
  quotes: RfqSubmission[];
  quoteTotal: number | null;
}

/** Structures the supplier can quote separately on this package (empty when fewer than two). */
export function vendorPackageStructureSlots(
  project: Pick<RfqProject, 'timberSupplierInvites'>,
  orgId: string | null | undefined,
  pkg: Pick<RfqPackage, 'scopeStructures' | 'proposalSnapshot' | 'cncPackagePayload'> | null | undefined,
  submissions: ReadonlyArray<RfqSubmission>,
): VendorStructureQuoteSlot[] {
  if (!orgId || !pkg) return [];
  const structures = timberQuoteAssignableStructures(project, orgId, pkg, null);
  if (structures.length < 2) return [];
  const live = submissions
    .filter(s => !s.notBidding)
    .slice()
    .sort((a, b) => (b.createdAt?.getTime?.() ?? 0) - (a.createdAt?.getTime?.() ?? 0));
  return structures.map(structure => {
    const quotes = live.filter(s => {
      const tagged = normalizeQuotedStructures(s.quotedStructures);
      return tagged.length === 1 && tagged[0].id === structure.id;
    });
    const latest = quotes[0] ?? null;
    const total = latest ? submissionQuoteTotal(latest) : null;
    return { structure, latest, quotes, quoteTotal: typeof total === 'number' && Number.isFinite(total) ? total : null };
  });
}

/** Quotes that cover the whole package (untagged) or several structures at once. */
export function vendorPackageWholeRequestQuotes(submissions: ReadonlyArray<RfqSubmission>): RfqSubmission[] {
  return submissions.filter(s => !s.notBidding && normalizeQuotedStructures(s.quotedStructures).length !== 1);
}
